/**
 * 滚动入场动画：让 .aw-anim 元素"进入视口才播放"动画
 *
 * 背景：纯 CSS animation 在页面加载时立即播放，视口外的元素
 * 滚动到面前时动画早已结束，看起来就是"没有动画"。
 *
 * 原理：
 * - IntersectionObserver 监听元素进入视口，进入时才加 .aw-anim--active 触发动画；
 * - MutationObserver 自动处理路由切换 / v-for 动态渲染出的新 .aw-anim 元素；
 * - 动画只播放一次，播完停止观察；
 * - 初始隐藏状态（.aw-anim--ready）也由 JS 加，JS 失效时内容正常显示。
 *
 * 用法：给任意元素加 class="aw-anim" 即可；
 * 可选：style="--aw-delay:.2s" 给同组元素设置错峰延迟。
 */

const READY_CLASS = "aw-anim--ready";
const ACTIVE_CLASS = "aw-anim--active";

function initScrollAnim() {
  // 老浏览器不支持 IntersectionObserver：不做处理，元素保持默认可见
  if (!("IntersectionObserver" in window)) return;

  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add(ACTIVE_CLASS);
          io.unobserve(entry.target); // 只播一次
        }
      });
    },
    {
      threshold: 0.15, // 元素露出 15% 即触发
      rootMargin: "0px 0px -8% 0px", // 距视口底部 8% 提前触发，避免贴边才动
    }
  );

  const bind = (el) => {
    if (el.__awBound) return;
    el.__awBound = true;
    el.classList.add(READY_CLASS); // 先隐藏，等进入视口再播
    io.observe(el);
  };

  const scan = (root) => {
    if (!root || root.nodeType !== 1) return;
    if (root.classList && root.classList.contains("aw-anim")) bind(root);
    if (root.querySelectorAll) {
      root.querySelectorAll(".aw-anim").forEach(bind);
    }
  };

  // 初始扫描（静态 HTML 里已有的元素）
  scan(document.body);

  // 持续监听 DOM 变化：路由切换、v-for 渲染的新元素自动生效
  new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach(scan));
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.body) {
  initScrollAnim();
} else {
  document.addEventListener("DOMContentLoaded", initScrollAnim);
}
